/** A reference designator-bearing module within a single design. */
interface DesignModuleRef {
	ref: string;
	name: string;
}

/** The subset of a design needed to resolve a module's design-level reference designator. */
interface ZoomTargetDesign {
	modelType: string;
	modules: DesignModuleRef[];
}

/** The subset of a project-level module needed to resolve its zoom target. */
interface ZoomTargetProjectModule {
	name: string;
	instances: Array<{ designId: string; ref: string }>;
}

/**
 * Resolves which design-level reference designator a whole-module chip click should zoom to.
 *
 * A project module (e.g. "U1") may be instantiated on several design sheets, each with its own
 * design-level name (e.g. "U1A", "U1B"). To keep the click feeling local, this prefers the instance
 * on the currently-active design, then the first schematic instance whose ref resolves, and finally falls back to the
 * project-level name when no instance can be resolved (rather than throwing).
 *
 * @param projectModule   - The project-level module, including its per-design instances.
 * @param activeDesignId  - The id of the currently-active design, if any.
 * @param designs         - Designs keyed by id, used to map an instance's `ref` to its design name.
 * @returns The design-level reference designator to zoom to, or the project-level name as a fallback.
 */
export function resolveModuleZoomTarget(
	projectModule: ZoomTargetProjectModule,
	activeDesignId: string | undefined,
	designs: Record<string, ZoomTargetDesign | undefined>,
): string {
	const activeInstance =
		activeDesignId !== undefined ? projectModule.instances.find((i) => i.designId === activeDesignId) : undefined;
	if (activeInstance !== undefined) {
		const designModule = designs[activeInstance.designId]?.modules.find((m) => m.ref === activeInstance.ref);
		if (designModule !== undefined) {
			return designModule.name;
		}
	}

	for (const instance of projectModule.instances) {
		if (designs[instance.designId]?.modelType !== 'schematic') {
			continue;
		}
		const designModule = designs[instance.designId]?.modules.find((m) => m.ref === instance.ref);
		if (designModule !== undefined) {
			return designModule.name;
		}
	}

	return projectModule.name;
}
